import {
  CONTRACTS,
  buildPath,
  type ContractId,
  type EndpointContract,
  type RequestBody,
  type RequestQuery,
  type ResponseOf,
} from '@shared/contracts';
import type { ApiErrorBody, ErrorCode } from '@shared/errors';
import { API_BASE_URL } from './api';

/**
 * The typed API client, driven entirely by the endpoint registry.
 *
 * Callers name a contract id, and the path, method, body type, query type and response
 * type all follow from it. There is no second place where a URL or a payload shape is
 * written down, so an endpoint cannot be called with the wrong body or read with the
 * wrong expectations — those are compile errors.
 */

const BASE = `${API_BASE_URL || ''}/api/v1`;

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | string,
    message: string,
    readonly status: number,
    readonly details?: { path: string; message: string }[],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors keyed by path, ready to hand to react-hook-form. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const d of this.details ?? []) {
      // Strip the `body.` / `query.` prefix the server adds.
      out[d.path.replace(/^(body|query|params)\./, '')] = d.message;
    }
    return out;
  }
}

// ── Session expiry handling ─────────────────────────────────────────────────────

type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler = () => undefined;

export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

/** Called when the server requires a password change before other work can continue. */
type PasswordChangeRequiredHandler = () => void;
let onPasswordChangeRequired: PasswordChangeRequiredHandler = () => undefined;

export function setPasswordChangeRequiredHandler(handler: PasswordChangeRequiredHandler): void {
  onPasswordChangeRequired = handler;
}

/**
 * The 401 codes that mean "this session is no longer usable".
 *
 * Everything else that answers 401 — a wrong current password, a locked account — is
 * about the request, not the session, and must reach the caller as an ordinary error.
 */
const SESSION_FAILURE_CODES = new Set<string>([
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'TOKEN_REUSE_DETECTED',
  'ACCOUNT_INACTIVE',
]);

/**
 * Single-flight refresh.
 *
 * Several requests failing with 401 at once must trigger ONE refresh, not one each:
 * parallel refreshes race to rotate the same token family, and the losers invalidate
 * the winner — logging the user out in the middle of a working session. Every caller
 * awaits the same promise.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so concurrent callers all observe the same result
      // before the next failure can start a second refresh.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

// ── CSRF ────────────────────────────────────────────────────────────────────────

/** The double-submit cookie is deliberately readable by JavaScript. */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)bemedic_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

// ── Request ─────────────────────────────────────────────────────────────────────

export interface CallOptions<K extends ContractId> {
  params?: Record<string, string | number>;
  query?: RequestQuery<K>;
  body?: RequestBody<K>;
  signal?: AbortSignal;
  /**
   * The file, for an endpoint the registry marks `upload: true`.
   *
   * It is sent as the RAW request body with its name in `x-file-name`, not as
   * multipart — see ADR-020. The consequence for callers is the one thing worth knowing
   * here: on an upload endpoint, `body` carries the METADATA and travels in the query
   * string, because the body itself is the file's bytes.
   */
  file?: File | Blob;
}

function buildUrl(
  contract: EndpointContract,
  options: {
    params?: Record<string, string | number>;
    query?: unknown;
    body?: unknown;
    upload?: boolean;
  },
): string {
  const path = buildPath(contract.path, options.params ?? {});
  const url = new URL(`${BASE}${path}`, window.location.origin);

  const parameters = {
    ...((options.query ?? {}) as Record<string, unknown>),
    // Upload metadata rides in the query string; see CallOptions.file.
    ...(options.upload ? ((options.body ?? {}) as Record<string, unknown>) : {}),
  };

  for (const [key, value] of Object.entries(parameters)) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    // A non-JSON error (a proxy error page, say) still has to become an ApiError so
    // callers have exactly one failure type to handle.
  }

  return new ApiError(
    body?.error.code ?? 'INTERNAL_ERROR',
    body?.error.message ?? `Request failed with status ${res.status}`,
    res.status,
    body?.error.details as { path: string; message: string }[] | undefined,
    body?.error.requestId,
  );
}

/**
 * Call an endpoint by its contract id.
 *
 * In development the response is parsed against the contract schema, so a server that
 * drifts from the contract fails here — at the boundary, with a readable path — rather
 * than as `undefined` three components deep.
 */
export async function callApi<K extends ContractId>(
  id: K,
  options: CallOptions<K> = {},
): Promise<ResponseOf<K>> {
  const contract = CONTRACTS[id] as EndpointContract;

  /*
   * Offline: a write is REFUSED, not queued.
   *
   * Nothing in the web app is saved for later — the one offline queue in the system is
   * the desktop's work-session events, which are safe only because they are
   * append-only, idempotent and reconciled by the server. Everything else needs a live
   * connection and current authorization at the moment it happens.
   *
   * Refusing here rather than in each screen is what makes that true of every mutation,
   * including ones written later. An employee who believes they punched in and did not
   * has a pay dispute, not a UX annoyance.
   */
  if (contract.method !== 'GET' && typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiError(
      'OFFLINE',
      'You are offline, so this could not be saved. Nothing has been recorded — try again once you are connected.',
      0,
    );
  }

  const upload = Boolean(contract.upload);
  const url = buildUrl(contract, { ...options, upload });

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    const hasBody = options.body !== undefined && !upload;

    if (hasBody) headers['content-type'] = 'application/json';

    if (upload && options.file) {
      // The browser will not set content-type for a bare Blob body, and the server
      // decides the real type from the file's magic bytes anyway — this is a hint only.
      headers['content-type'] = options.file.type || 'application/octet-stream';
      headers['x-file-name'] =
        options.file instanceof File ? encodeURIComponent(options.file.name) : 'upload';
    }

    if (contract.method !== 'GET') {
      const token = csrfToken();
      if (token) headers['x-csrf-token'] = token;
    }

    return fetch(url, {
      method: contract.method,
      credentials: 'include',
      headers,
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(upload && options.file ? { body: options.file } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  };

  let res = await send();

  if (res.status === 401 && contract.auth) {
    const error = await parseError(res);

    if (SESSION_FAILURE_CODES.has(error.code)) {
      const refreshed = await refreshSession();
      if (refreshed) {
        res = await send();
      } else {
        // The session is genuinely gone. Clearing client state here — rather than
        // letting the caller decide — is what stops a logged-out user sitting on a
        // populated screen until they happen to reload.
        onSessionExpired();
        throw new ApiError('UNAUTHENTICATED', 'Your session has expired', 401);
      }
    } else {
      // Not every 401 is about the session. "Your current password is incorrect" is a
      // 401 too, and treating it as an expired session would sign the user out for
      // mistyping a field.
      throw error;
    }
  }

  if (!res.ok) {
    const error = await parseError(res);
    if (error.code === 'PASSWORD_CHANGE_REQUIRED') {
      onPasswordChangeRequired();
    }
    throw error;
  }
  if (res.status === 204) return undefined as ResponseOf<K>;

  const payload: unknown = await res.json();

  if (import.meta.env.DEV) {
    const result = contract.response.safeParse(payload);
    if (!result.success) {
      // eslint-disable-next-line no-console
      console.error(
        `[api] Response for "${id}" does not match its contract:`,
        result.error.issues.slice(0, 5).map((i) => `${i.path.join('.')}: ${i.message}`),
      );
    }
  }

  return payload as ResponseOf<K>;
}

/** Download an endpoint that returns a file rather than JSON. */
export async function downloadApi<K extends ContractId>(
  id: K,
  options: CallOptions<K> = {},
): Promise<Blob> {
  const contract = CONTRACTS[id] as EndpointContract;
  const res = await fetch(buildUrl(contract, options), {
    method: contract.method,
    credentials: 'include',
  });

  if (!res.ok) throw await parseError(res);
  return res.blob();
}
